import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card, Badge, Button, GradientBar, EmptyState } from "@/components/design-system";
import { TID } from "@/lib/testIds";
import { api } from "@/lib/api";
import { listOwnProducts } from "@/lib/products";

function StatCard({ label, value, sub }) {
  return (
    <Card className="!p-6">
      <div className="text-[11px] font-bold uppercase tracking-widest text-[#0B1F3A]/60">{label}</div>
      <div className="font-[Montserrat] font-extrabold text-3xl mt-2 text-[#0B1F3A] tracking-tighter">{value}</div>
      {sub && <div className="text-xs text-[#0B1F3A]/60 mt-1">{sub}</div>}
    </Card>
  );
}

function Header({ eyebrow, title, subtitle }) {
  return (
    <div className="mb-8">
      <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#2D7FF9]">{eyebrow}</p>
      <h1 className="font-[Montserrat] font-extrabold text-3xl md:text-4xl tracking-tight text-[#0B1F3A] mt-2">{title}</h1>
      {subtitle && <p className="text-[#0B1F3A]/70 mt-2 max-w-2xl">{subtitle}</p>}
    </div>
  );
}

function FounderHome({ user }) {
  const [products, setProducts] = useState({ status: "loading", items: [] });
  useEffect(() => {
    listOwnProducts()
      .then((items) => setProducts({ status: "ready", items }))
      .catch(() => setProducts({ status: "ready", items: [] }));
  }, []);
  const total = products.items.length;
  const published = products.items.filter((p) => p.status === "published").length;
  return (
    <div data-testid={TID.dashboard.founderHome}>
      <Header eyebrow={`Welcome back, ${user.name.split(" ")[0] || "founder"}`} title="Your launch loop starts here." subtitle="Cycle C2 is live — add products and open them to the community. Campaigns and feedback reports arrive in Cycles C3 and C4." />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Products" value={total} sub={published ? `${published} published` : "0 published"} />
        <StatCard label="Active campaigns" value="0" sub="Cycle C3 →" />
        <StatCard label="Feedback received" value="0" sub="Cycle C3 →" />
        <StatCard label="Feedback reports" value="0" sub="Cycle C4 →" />
      </div>
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <div className="flex items-center gap-2 mb-4"><Badge variant="ai">Cycle C2 · Live</Badge></div>
          <h3 className="font-[Montserrat] font-bold text-2xl text-[#0B1F3A]">
            {total === 0 ? "Add your first product" : "Your products at a glance"}
          </h3>
          {total === 0 ? (
            <>
              <p className="mt-3 text-[#0B1F3A]/75 leading-relaxed">Publish a product to open it to the community and start collecting qualified feedback in Cycle C3.</p>
              <div className="mt-6"><Link to="/app/products/new"><Button variant="primary" data-testid="founder-cta-add-product">Add a product</Button></Link></div>
            </>
          ) : (
            <>
              <ul className="mt-4 space-y-3">
                {products.items.slice(0, 4).map((p) => (
                  <li key={p.id} className="flex items-center justify-between gap-3 border-b border-[#0B1F3A]/10 pb-3 last:border-b-0">
                    <div className="min-w-0">
                      <div className="font-semibold text-[#0B1F3A] truncate">{p.name}</div>
                      <div className="text-xs text-[#0B1F3A]/60 truncate">{p.tagline}</div>
                    </div>
                    <Badge variant={p.status === "published" ? "active" : "neutral"}>{p.status}</Badge>
                  </li>
                ))}
              </ul>
              <div className="mt-5 flex gap-3"><Link to="/app/products"><Button variant="secondary">Manage products</Button></Link><Link to="/app/products/new"><Button variant="primary">Add product</Button></Link></div>
            </>
          )}
        </Card>
        <Card>
          <GradientBar className="rounded-full mb-4" />
          <h4 className="font-[Montserrat] font-bold text-lg text-[#0B1F3A]">Loop progress</h4>
          <ul className="mt-3 space-y-2 text-sm">
            {["Validate", "Improve", "Launch", "Promote", "Grow"].map((s, i) => (
              <li key={s} className="flex items-center gap-3">
                <span className="w-6 h-6 rounded-full bg-[#E8F1FF] text-[#2D7FF9] text-xs font-bold flex items-center justify-center">{i+1}</span>
                <span className="text-[#0B1F3A]/85">{s}</span>
                <span className="ml-auto text-[10px] uppercase tracking-widest text-[#0B1F3A]/50">{i === 0 && total > 0 ? "in progress" : "soon"}</span>
              </li>
            ))}
          </ul>
        </Card>
      </div>
    </div>
  );
}

function CommunityHome({ user }) {
  const pts = user.reputation_points || 0;
  const tierLabel = user.tier === "ambassador_eligible" ? "Ambassador eligible" : user.tier === "validator" ? "Validator" : "Explorer";

  // Real thresholds live in backend/app/reputation.py — Validator 25 pts, Ambassador eligible 100 pts.
  const VALIDATOR_AT = 25;
  const AMBASSADOR_AT = 100;
  const nextTierValue = user.tier === "ambassador_eligible" ? "—" : (user.tier === "validator" ? AMBASSADOR_AT : VALIDATOR_AT);
  const nextTierSub = user.tier === "ambassador_eligible"
    ? "Top tier"
    : `${Math.max(0, (user.tier === "validator" ? AMBASSADOR_AT : VALIDATOR_AT) - pts)} pts to go`;

  const [stats, setStats] = useState({ productsLive: null, mySubmissions: null, application: null });
  useEffect(() => {
    (async () => {
      try {
        const [productsRes, subsRes, appRes] = await Promise.allSettled([
          api.get("/products/public?page=1&page_size=1"),
          api.get("/campaigns/mine/submissions"),
          api.get("/ambassadors/me"),
        ]);
        setStats({
          productsLive: productsRes.status === "fulfilled" ? (productsRes.value.data?.total ?? 0) : null,
          mySubmissions: subsRes.status === "fulfilled" ? (subsRes.value.data?.items?.length ?? 0) : null,
          application: appRes.status === "fulfilled" ? (appRes.value.data?.application || null) : null,
        });
      } catch { /* keep placeholder dashes */ }
    })();
  }, []);

  const isVerifiedAmbassador = user.ambassador_status === "verified";
  const appStatus = stats.application?.status;
  const ambassadorStatusLabel = isVerifiedAmbassador
    ? "Verified"
    : appStatus === "scoring" ? "AI scoring"
    : appStatus === "scored" ? "Awaiting review"
    : appStatus === "rejected" ? "Rejected"
    : appStatus === "score_failed" ? "Rescore needed"
    : user.tier === "ambassador_eligible" ? "Eligible — apply"
    : "Not applied";
  const ambassadorStatusSub = isVerifiedAmbassador
    ? "Referral codes live"
    : user.tier === "ambassador_eligible" ? `Apply now` : `Reach ${AMBASSADOR_AT} pts`;

  const dash = (v) => (v === null || v === undefined ? "—" : v);

  return (
    <div data-testid={TID.dashboard.communityHome}>
      <Header
        eyebrow={`Hi, ${user.name.split(" ")[0] || "there"}`}
        title="Give qualified feedback. Build your reputation."
        subtitle="Every helpful review counts. Climb Explorer → Validator → Ambassador eligible, then apply as a Verified Ambassador and earn 12.5% recurring on every subscription you bring in."
      />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Reputation" value={pts} sub={tierLabel} />
        <StatCard label="Next tier at" value={nextTierValue} sub={nextTierSub} />
        <StatCard label="Products live" value={dash(stats.productsLive)} sub={<Link to="/app/explore" className="text-[#2D7FF9] font-semibold">Explore now →</Link>} />
        <StatCard label="Ambassador status" value={ambassadorStatusLabel} sub={ambassadorStatusSub} />
      </div>
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <Badge variant="community">What you can do here</Badge>
          <h3 className="font-[Montserrat] font-bold text-2xl text-[#0B1F3A] mt-3">Your path to Verified Ambassador</h3>
          <ul className="mt-4 space-y-3 text-sm text-[#0B1F3A]/85">
            <li className="flex gap-3">
              <span className="mt-0.5 flex-shrink-0 w-6 h-6 rounded-full bg-[#E8F1FF] text-[#2D7FF9] text-xs font-bold flex items-center justify-center">1</span>
              <div><b className="text-[#0B1F3A]">Explore products</b> and open the ones you know something about. {stats.productsLive != null && stats.productsLive > 0 && (<span className="text-[#0B1F3A]/60">{stats.productsLive} product{stats.productsLive === 1 ? "" : "s"} live right now.</span>)}</div>
            </li>
            <li className="flex gap-3">
              <span className="mt-0.5 flex-shrink-0 w-6 h-6 rounded-full bg-[#E8F1FF] text-[#2D7FF9] text-xs font-bold flex items-center justify-center">2</span>
              <div><b className="text-[#0B1F3A]">Submit structured feedback</b> on their open validation campaigns. {stats.mySubmissions != null && (<span className="text-[#0B1F3A]/60">You&apos;ve submitted <b className="text-[#0B1F3A]">{stats.mySubmissions}</b> so far.</span>)}</div>
            </li>
            <li className="flex gap-3">
              <span className="mt-0.5 flex-shrink-0 w-6 h-6 rounded-full bg-[#E8F1FF] text-[#2D7FF9] text-xs font-bold flex items-center justify-center">3</span>
              <div><b className="text-[#0B1F3A]">Earn reputation</b> — <b className="text-[#2D7FF9]">{VALIDATOR_AT} pts</b> unlocks Validator, <b className="text-[#2D7FF9]">{AMBASSADOR_AT} pts</b> unlocks the Ambassador application.</div>
            </li>
            <li className="flex gap-3">
              <span className="mt-0.5 flex-shrink-0 w-6 h-6 rounded-full bg-[#E8F1FF] text-[#2D7FF9] text-xs font-bold flex items-center justify-center">4</span>
              <div><b className="text-[#0B1F3A]">Apply as an Ambassador</b> — AI scores your application, an admin approves, and you get a unique per-product referral link paying <b>12.5% for 12 months</b>.</div>
            </li>
          </ul>
          <div className="mt-6 flex flex-wrap gap-2">
            <Link to="/app/explore"><Button variant="primary" data-testid="community-cta-explore">Explore products</Button></Link>
            <Link to="/app/reputation"><Button variant="secondary" data-testid="community-cta-reputation">My reputation</Button></Link>
            <Link to="/app/ambassadors"><Button variant="ghost" data-testid="community-cta-ambassador">Ambassador dashboard</Button></Link>
          </div>
          <div className="mt-8 pt-6 border-t border-[#0B1F3A]/10">
            <h4 className="font-[Montserrat] font-bold text-lg text-[#0B1F3A]">Point rules</h4>
            <ul className="mt-4 space-y-3 text-sm text-[#0B1F3A]/85">
              <li className="flex gap-3"><span className="font-bold text-[#2D7FF9] w-8">+2</span> Structured feedback submitted</li>
              <li className="flex gap-3"><span className="font-bold text-[#2D7FF9] w-8">+5</span> Feedback marked &quot;helpful&quot; by a founder</li>
              <li className="flex gap-3"><span className="font-bold text-[#2D7FF9] w-8">+10</span> Feedback cited in an AI insight report</li>
              <li className="flex gap-3"><span className="font-bold text-red-600 w-8">−5</span> Feedback removed by admin</li>
            </ul>
          </div>
        </Card>
        <Card>
          <h4 className="font-[Montserrat] font-bold text-lg text-[#0B1F3A]">Next step</h4>
          {isVerifiedAmbassador ? (
            <p className="mt-2 text-sm text-[#0B1F3A]/75">You&apos;re a <b>Verified Ambassador</b>. Your per-product referral links are live — share them and earn 12.5% for 12 months.</p>
          ) : pts < VALIDATOR_AT ? (
            <p className="mt-2 text-sm text-[#0B1F3A]/75">You&apos;re <b className="text-[#0B1F3A]">{VALIDATOR_AT - pts} pts</b> away from <b>Validator</b>. Submit structured feedback on any open campaign to start climbing.</p>
          ) : pts < AMBASSADOR_AT ? (
            <p className="mt-2 text-sm text-[#0B1F3A]/75">You&apos;re a <b>Validator</b>. Reach <b className="text-[#0B1F3A]">{AMBASSADOR_AT} pts</b> ({AMBASSADOR_AT - pts} more) to apply as a Verified Ambassador.</p>
          ) : appStatus ? (
            <p className="mt-2 text-sm text-[#0B1F3A]/75">Your application is <b>{ambassadorStatusLabel.toLowerCase()}</b>. Check the Ambassador tab for details.</p>
          ) : (
            <p className="mt-2 text-sm text-[#0B1F3A]/75">You&apos;re eligible — head to the <b>Ambassador</b> tab to submit your application.</p>
          )}
          <div className="mt-4">
            {!isVerifiedAmbassador && pts < AMBASSADOR_AT ? (
              <Link to="/app/explore"><Button variant="primary" data-testid="community-next-cta">Give feedback</Button></Link>
            ) : (
              <Link to="/app/ambassadors"><Button variant="primary" data-testid="community-next-cta">Open Ambassador</Button></Link>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

function AdminHome({ user }) {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/admin/moderation/summary").then((r) => setStats(r.data)).catch(() => setStats(null));
  }, []);
  const s = stats || {};
  return (
    <div data-testid={TID.dashboard.adminHome}>
      <Header
        eyebrow="Administrator"
        title="Platform integrity dashboard."
        subtitle="Live counters for every core surface — users, products, campaigns, moderation queue, ambassador applications and active subscriptions. Jump straight into any of them below."
      />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Users" value={s.users ?? "—"} sub={<Link to="/app/admin/users" className="text-[#2D7FF9] font-semibold">Manage users →</Link>} />
        <StatCard label="Products live" value={s.products ?? "—"} sub={`${s.open_campaigns ?? 0} open campaign${(s.open_campaigns ?? 0) === 1 ? "" : "s"}`} />
        <StatCard label="Feedback approved" value={s.submissions_approved ?? "—"} sub={<Link to="/app/admin/moderation" className="text-[#2D7FF9] font-semibold">{s.submissions_hidden ?? 0} hidden →</Link>} />
        <StatCard label="Active subscriptions" value={s.active_subscriptions ?? "—"} sub={<Link to="/app/admin/payouts" className="text-[#2D7FF9] font-semibold">Open payouts →</Link>} />
      </div>
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card>
          <h3 className="font-[Montserrat] font-bold text-lg text-[#0B1F3A]">Users by role</h3>
          <ul className="mt-3 space-y-2 text-sm text-[#0B1F3A]/85">
            {Object.entries(s.users_by_role || {}).map(([role, count]) => (
              <li key={role} className="flex items-center justify-between"><span className="capitalize">{role}</span><b className="text-[#0B1F3A] tabular-nums">{count}</b></li>
            ))}
          </ul>
        </Card>
        <Card>
          <h3 className="font-[Montserrat] font-bold text-lg text-[#0B1F3A]">Community tiers</h3>
          <ul className="mt-3 space-y-2 text-sm text-[#0B1F3A]/85">
            {Object.entries(s.users_by_tier || {}).map(([tier, count]) => (
              <li key={tier} className="flex items-center justify-between"><span className="capitalize">{tier.replace(/_/g, " ")}</span><b className="text-[#0B1F3A] tabular-nums">{count}</b></li>
            ))}
            <li className="flex items-center justify-between pt-2 border-t border-[#0B1F3A]/10"><span>Verified ambassadors</span><b className="text-[#2D7FF9] tabular-nums">{s.ambassadors_verified ?? 0}</b></li>
          </ul>
        </Card>
        <Card>
          <h3 className="font-[Montserrat] font-bold text-lg text-[#0B1F3A]">Ambassador applications</h3>
          <div className="mt-3 space-y-2 text-sm text-[#0B1F3A]/85">
            <div className="flex items-center justify-between"><span>Pending review</span><b className="text-[#0B1F3A] tabular-nums">{s.ambassador_applications_pending ?? 0}</b></div>
            <div className="flex items-center justify-between"><span>Ready to approve</span><b className="text-[#2D7FF9] tabular-nums">{s.ambassador_applications_scored_ready ?? 0}</b></div>
          </div>
          <div className="mt-4"><Link to="/app/admin/ambassadors"><Button variant="primary" data-testid="admin-cta-ambassadors">Open queue</Button></Link></div>
        </Card>
      </div>
      <div className="mt-8">
        <Card>
          <h3 className="font-[Montserrat] font-bold text-xl text-[#0B1F3A]">Security posture</h3>
          <ul className="mt-3 space-y-2 text-sm">
            <li className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-[#7ED957]" /> Passwords hashed with Argon2id.</li>
            <li className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-[#7ED957]" /> Access + refresh tokens in httpOnly cookies with rotation & reuse detection.</li>
            <li className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-[#7ED957]" /> Brute-force lockout after 5 failed attempts.</li>
          </ul>
        </Card>
      </div>
    </div>
  );
}

function AmbassadorHome({ user }) {
  return (
    <div data-testid={TID.dashboard.ambassadorHome}>
      <Header eyebrow="Ambassador" title="Recurring earnings, built to last." subtitle="Referral tools and Stripe Connect payouts arrive in Cycle C6 and P3." />
      <EmptyState title="Cycle C6 unlocks this workspace" description="Once ambassador application + AI scoring ships, you'll get unique referral links here and see live earnings." action={<Link to="/app"><Button variant="primary">Back to overview</Button></Link>} />
    </div>
  );
}

export default function DashboardHome() {
  const { user } = useAuth();
  if (!user) return null;
  const role = user.active_role || user.roles?.[0];
  if (role === "founder") return <FounderHome user={user} />;
  if (role === "community") return <CommunityHome user={user} />;
  if (role === "admin") return <AdminHome user={user} />;
  if (role === "ambassador") return <AmbassadorHome user={user} />;
  return <CommunityHome user={user} />;
}
